import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import ShiftAndTimeService from "@/services/ShiftAndTime/ShiftAndTime";
import { ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import type { shiftAndTime } from "@/interface/shiftAndTime.interface";
import { RoutesLink } from "@/utility/RouteLinks";
import { Button } from "@/components/ui/button";
import {
    Clock,
    Coffee,
    AlarmClock,
    Briefcase,
    LogIn,
    LogOut,
    Pencil,
} from "lucide-react";
import FullPageLoader from "@/components/common/FullPageLoader";

// ── Helper: format "HH:MM:SS" → "HH:MM AM/PM" ────────────────────────────────
function formatTime(time?: string): string {
    if (!time) return "—";
    const [h, m] = time.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return time;
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
}

// ── Helper: format duration "HH:MM:SS" → "X mins" ───────────────────────────
function formatBuffer(time?: string): string {
    if (!time) return "—";
    const parts = time.split(":").map(Number);
    if (parts.length < 2 || parts.some(isNaN)) return time;
    const hours = parts[0];
    const minutes = parts[1];
    const totalMinutes = hours * 60 + minutes;
    return `${totalMinutes} mins`;
}

// ── Detail card item ──────────────────────────────────────────────────────────
interface InfoRowProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    accent?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, accent = "bg-primary/10 text-primary" }) => (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${accent}`}>
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-400 font-medium">{label}</p>
            <p className="text-sm font-semibold text-gray-800 mt-0.5">{value}</p>
        </div>
    </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const ShiftAndTimeDetail: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [shift, setShift] = useState<shiftAndTime | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchShift = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const response = await ShiftAndTimeService.getShiftAndTimeById(id);
            if (response?.status) {
                setShift(response.data);
            } else {
                showToastNotification("danger", response?.message);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchShift();
    }, [id]);

    if (isLoading) {
        return (
            <Layout>
                <FullPageLoader />
            </Layout>
        )
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between gap-4">
                    <MainHeading
                        title="Shift Detail"
                        isBackButtonVisible
                        onClick={() => navigate(-1)}
                    />
                    <Button
                        onClick={() => navigate(`${RoutesLink.editShiftAndTime}/${id}`)}
                        className="flex items-center gap-2 shrink-0"
                    >
                        <Pencil size={15} />
                        Edit Shift
                    </Button>
                </div>

                {isLoading ? (
                    <Card>
                        <div className="flex items-center justify-center py-16">
                            <span className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    </Card>
                ) : shift ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* ── Left: Summary Card ── */}
                        <Card className="lg:col-span-1 flex flex-col items-center justify-center text-center py-8 gap-3">
                            <div className="h-16 w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-2">
                                <Clock size={32} />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 capitalize">{shift.shiftName}</h2>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border capitalize
                                bg-blue-50 text-blue-700 border-blue-200">
                                <Briefcase size={12} />
                                {shift.type}
                            </span>
                            <p className="text-sm text-gray-400 mt-1">
                                {formatTime(shift.shiftStartTime)} — {formatTime(shift.shiftEndTime)}
                            </p>
                        </Card>

                        {/* ── Right: Details Grid ── */}
                        <Card className="lg:col-span-2">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                                Shift Timings
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                                <InfoRow
                                    icon={<LogIn size={18} />}
                                    label="Shift Start Time"
                                    value={formatTime(shift.shiftStartTime)}
                                    accent="bg-green-100 text-green-700"
                                />
                                <InfoRow
                                    icon={<LogOut size={18} />}
                                    label="Shift End Time"
                                    value={formatTime(shift.shiftEndTime)}
                                    accent="bg-red-100 text-red-600"
                                />
                                <InfoRow
                                    icon={<Coffee size={18} />}
                                    label="Break Start Time"
                                    value={formatTime(shift.breakStartTime)}
                                    accent="bg-amber-100 text-amber-700"
                                />
                                <InfoRow
                                    icon={<Coffee size={18} />}
                                    label="Break End Time"
                                    value={formatTime(shift.breakEndTime)}
                                    accent="bg-amber-100 text-amber-700"
                                />
                                <InfoRow
                                    icon={<AlarmClock size={18} />}
                                    label="Check-in Buffer"
                                    value={formatBuffer(shift.checkInBufferTime)}
                                    accent="bg-purple-100 text-purple-700"
                                />
                                <InfoRow
                                    icon={<AlarmClock size={18} />}
                                    label="Check-out Buffer"
                                    value={formatBuffer(shift.checkOutBufferTime)}
                                    accent="bg-purple-100 text-purple-700"
                                />

                            </div>
                        </Card>

                    </div>
                ) : (
                    <Card>
                        <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
                            Shift not found.
                        </div>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default ShiftAndTimeDetail;
